import type { ReactNode } from 'react';
import { Marquee } from './Marquee';
import { useScrollScene } from '../hooks/useScrollScene';

export type Tone = 'paper' | 'mist' | 'red' | 'ink' | 'night';

/** Page-level tone names map onto the surface set in index.css. */
const TONE_CLASS: Record<Tone, string> = {
  paper: 'tone-aurora',
  mist: 'tone-deep',
  red: 'tone-brand',
  ink: 'tone-void',
  night: 'tone-nebula',
};

type SectionProps = {
  tone?: Tone;
  id?: string;
  hero?: boolean;
  bleed?: boolean;
  /** Pins the decorative ticker to the bottom edge of the panel. */
  marquee?: boolean;
  /** 0 disables the 3D scroll effect for this section. */
  depth?: number;
  className?: string;
  children: ReactNode;
};

export function Section({
  tone = 'paper',
  id,
  hero = false,
  bleed = false,
  marquee = false,
  depth = 1,
  className = '',
  children,
}: SectionProps) {
  const sceneRef = useScrollScene<HTMLElement>(depth);

  return (
    <section
      ref={sceneRef}
      id={id}
      className={`${TONE_CLASS[tone]} siv-panel scene-panel scroll-mt-24 ${
        hero ? 'lg:min-h-[min(84vh,54rem)]' : ''
      } mb-5 flex flex-col px-5 py-14 sm:px-8 sm:py-16 lg:px-14 lg:py-24 ${
        marquee ? 'pb-24 lg:pb-28' : ''
      } ${className}`}
    >
      <div
        className={`relative z-10 flex flex-1 flex-col justify-center ${
          bleed ? '' : 'mx-auto w-full max-w-[78rem]'
        }`}
      >
        {children}
      </div>

      {marquee && (
        <div className="absolute inset-x-0 bottom-0 z-10">
          <Marquee />
        </div>
      )}
    </section>
  );
}
